import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import * as Body from '../Styles';
import * as functions from '../../../utils/functions/functions';
import * as Regex from '../../../utils/Regex/Regex';
import ListModal from '../../../components/ListModal/ListModal';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { USE_MOCK } from '../../../utils/mock/mockFlag';
import { mockAutotuning } from '../../../utils/mock/mockData';
import { getApiBaseUrl } from '../../../utils/defines/apiUrl';

// ── Tipos ─────────────────────────────────────────────────────────────

type HistoricoItem = {
    id: number;
    criado_em: string;
    equipamento: string;
    parametro: string;
    valor_anterior: string | null;
    valor_novo: string;
    taxa_calculada: number | null;
    motivo: string | null;
};

type AutotuningData = {
    status: number; // 0 = desativado, 1 = ativo, 2 = desativado pela equipe interna
    historico: HistoricoItem[];
};

type Props = {
    toggleModal: Dispatch<SetStateAction<boolean>>;
    token: string;
};

type ParamSnapshot = {
    equipamento: string;
    parametro: string;
    valor: string;
};

// ── Labels e formatadores ─────────────────────────────────────────────

const PARAM_LABEL: Record<string, string> = {
    seletor_bomba: 'Seletor de Bomba',
    tol_sup:       'Tol. Superior',
    tol_inf:       'Tol. Inferior',
    deltaz:        'Delta Z',
};

const SELETOR_LABEL: Record<string, string> = {
    '1': 'Bomba 1',
    '2': 'Bomba 2',
    '3': 'Bomba 3',
    '4': 'Todas',
};

function formatKpiValue(parametro: string, valor: string): string {
    if (parametro === 'seletor_bomba') return SELETOR_LABEL[valor] ?? `Bomba ${valor}`;
    const n = parseFloat(valor);
    if (isNaN(n)) return valor;
    if (parametro === 'deltaz')  return `${n.toFixed(1)} mm`;
    // tol_sup / tol_inf: mostrar como porcentagem do setpoint (ex: 1.035 → "+3.5%")
    const pct = ((n - 1.0) * 100);
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

// ── Helpers ───────────────────────────────────────────────────────────

function getStatusLabel(statusCode: number): string {
    if (statusCode === 1) return 'Ativado';
    if (statusCode === 2) return 'Desativado pela equipe interna';
    return 'Desativado';
}

function getEquipamentoLabel(equip: string): string {
    if (equip.startsWith('uh')) return `UH ${equip.slice(2)}`;
    if (equip.startsWith('t'))  return `Terno ${equip.slice(1)}`;
    return equip.toUpperCase();
}

function formatDate(isoStr: string): string {
    const { dia, mes, ano, hora } = Regex.convertUTCToBrasilia(isoStr);
    return `${dia}/${mes}/${ano} ${hora}`;
}

/**
 * Deriva um snapshot dos valores atuais por equipamento+parâmetro
 * tomando a entrada mais recente de cada combinação no histórico.
 */
function deriveCurrentParams(historico: HistoricoItem[]): ParamSnapshot[] {
    const seen = new Set<string>();
    const result: ParamSnapshot[] = [];
    // historico já vem ordenado DESC do backend
    for (const item of historico) {
        const key = `${item.equipamento}|${item.parametro}`;
        if (!seen.has(key)) {
            seen.add(key);
            result.push({
                equipamento: item.equipamento,
                parametro:   item.parametro,
                valor:       item.valor_novo,
            });
        }
    }
    // Ordenar: UH antes de ternos, dentro de cada equipamento: seletor → tol_sup → tol_inf → deltaz
    const PARAM_ORDER: Record<string, number> = { seletor_bomba: 0, tol_sup: 1, tol_inf: 2, deltaz: 3 };
    result.sort((a, b) => {
        // UH < Terno
        const aIsUH = a.equipamento.startsWith('uh');
        const bIsUH = b.equipamento.startsWith('uh');
        if (aIsUH !== bIsUH) return aIsUH ? -1 : 1;
        if (a.equipamento !== b.equipamento) return a.equipamento.localeCompare(b.equipamento);
        return (PARAM_ORDER[a.parametro] ?? 99) - (PARAM_ORDER[b.parametro] ?? 99);
    });
    return result;
}

// ── Estilos locais ────────────────────────────────────────────────────

const StatusBadge = styled.span<{ statusCode: number }>`
    display: inline-block;
    padding: 3px 12px;
    border-radius: 8px;
    font-family: 'Nunito', sans-serif;
    font-size: 12px;
    font-weight: 700;
    background: ${({ statusCode }) =>
        statusCode === 1 ? 'rgba(92, 185, 49, 0.15)' :
        statusCode === 2 ? 'rgba(240, 63, 63, 0.12)' :
                           'rgba(232, 168, 76, 0.15)'};
    color: ${({ statusCode }) =>
        statusCode === 1 ? '#5CB931' :
        statusCode === 2 ? '#F03F3F' :
                           '#E8A84C'};
    border: 1px solid ${({ statusCode }) =>
        statusCode === 1 ? 'rgba(92, 185, 49, 0.4)' :
        statusCode === 2 ? 'rgba(240, 63, 63, 0.3)' :
                           'rgba(232, 168, 76, 0.4)'};
`;

const ParamBadge = styled.span`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 8px;
    font-family: 'Nunito', sans-serif;
    font-size: 11px;
    font-weight: 700;
    background: rgba(94, 94, 94, 0.15);
    color: rgba(255, 255, 255, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.1);
`;

// ── KPI cards ─────────────────────────────────────────────────────────

const KpiGrid = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;
`;

const KpiCard = styled.div`
    background: rgba(94, 94, 94, 0.12);
    backdrop-filter: blur(4px);
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 80px;
    flex: 1 1 80px;
    max-width: 140px;
    box-sizing: border-box;
`;

const KpiEquip = styled.span`
    font-family: 'Nunito', sans-serif;
    font-size: 10px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.35);
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 2px;
`;

const KpiLabel = styled.span`
    font-family: 'Nunito', sans-serif;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 4px;
`;

const KpiValue = styled.span`
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    white-space: nowrap;
`;

// ── Cards de histórico ────────────────────────────────────────────────

const HistoricoCard = styled.div`
    background: rgba(94, 94, 94, 0.1);
    backdrop-filter: blur(4px);
    border-radius: 14px;
    width: 100%;
    margin-bottom: 6px;
    padding: 10px 18px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 0 8px;
    width: 100%;
`;

const ValueArrow = styled.span`
    font-family: 'Montserrat', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
`;

const EmptyMsg = styled.p`
    font-family: 'Nunito', sans-serif;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.4);
    text-align: center;
    margin-top: 32px;
    width: 100%;
`;

const SectionLabel = styled.p`
    font-family: 'Nunito', sans-serif;
    font-size: 11px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.3);
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin: 0 0 8px 2px;
`;

// ── Componente ────────────────────────────────────────────────────────

const AutotuningModal: React.FC<Props> = ({ toggleModal, token }) => {
    const dispatch  = useDispatch();
    const navigate  = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData]       = useState<AutotuningData | null>(null);

    useEffect(() => {
        if (USE_MOCK) {
            setData(mockAutotuning as AutotuningData);
            setLoading(false);
            return;
        }

        axios
            .get(`${getApiBaseUrl()}/autotuning?token=${token}`, {
                withCredentials: true,
            })
            .then((res) => {
                setData(res.data);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                functions.ResolveErrors(
                    err.response?.status,
                    err.response?.data?.code,
                    err.response?.data?.message,
                    navigate,
                    dispatch,
                    err.response?.data?.content
                );
            });
    }, []);

    const currentParams = data ? deriveCurrentParams(data.historico) : [];

    return (
        <ListModal
            title="Auto Tuning"
            toggleModal={toggleModal}
            loading={loading}
            zindex={2}
        >
            <Body.Scroll>
                {/* ── Status badge ── */}
                {!loading && data && (
                    <div style={{ marginBottom: 16 }}>
                        <StatusBadge statusCode={data.status}>
                            {getStatusLabel(data.status)}
                        </StatusBadge>
                    </div>
                )}

                {/* ── KPI — valores atuais parametrizados ── */}
                {!loading && currentParams.length > 0 && (
                    <>
                        <SectionLabel>Valores atuais</SectionLabel>
                        <SectionLabel style={{textTransform:'none'}}>Definidos pelo Auto Tuning</SectionLabel>
                        <KpiGrid>
                            {currentParams.map((p) => (
                                <KpiCard key={`${p.equipamento}|${p.parametro}`}>
                                    <KpiEquip>{getEquipamentoLabel(p.equipamento)}</KpiEquip>
                                    <KpiLabel>{PARAM_LABEL[p.parametro] ?? p.parametro}</KpiLabel>
                                    <KpiValue>{formatKpiValue(p.parametro, p.valor)}</KpiValue>
                                </KpiCard>
                            ))}
                        </KpiGrid>
                    </>
                )}

                {/* ── Histórico de ajustes ── */}
                {!loading && data && data.historico.length > 0 && (
                    <SectionLabel style={{ marginTop: 4 }}>Histórico de ajustes</SectionLabel>
                )}

                {!loading && data && data.historico.length === 0 && (
                    <EmptyMsg>Nenhum ajuste registrado ainda.</EmptyMsg>
                )}

                {!loading && data && data.historico.map((item) => (
                    <HistoricoCard key={item.id}>
                        <Row style={{ marginBottom: 4 }}>
                            <Body.ListTitle style={{ fontSize: 15 }}>
                                {getEquipamentoLabel(item.equipamento)}
                            </Body.ListTitle>
                            <ParamBadge>
                                {PARAM_LABEL[item.parametro] ?? item.parametro}
                            </ParamBadge>
                        </Row>
                        <Row>
                            {item.valor_anterior !== null && (
                                <>
                                    <ValueArrow>{item.valor_anterior}</ValueArrow>
                                    <Body.ListText style={{ margin: 0, fontSize: 13 }}>→</Body.ListText>
                                </>
                            )}
                            <ValueArrow>{item.valor_novo}</ValueArrow>
                        </Row>
                        {item.taxa_calculada !== null && (
                            <Body.ListText style={{ fontSize: 12, marginBottom: 2 }}>
                                Taxa: {item.taxa_calculada.toFixed(3)} ac/min
                            </Body.ListText>
                        )}
                        {item.motivo && (
                            <Body.ListText style={{ fontSize: 12, marginBottom: 2 }}>
                                {item.motivo}
                            </Body.ListText>
                        )}
                        <Body.ListText style={{ fontSize: 11, opacity: 0.55, marginBottom: 0 }}>
                            {formatDate(item.criado_em)}
                        </Body.ListText>
                    </HistoricoCard>
                ))}
            </Body.Scroll>
        </ListModal>
    );
};

export default AutotuningModal;
